import React, { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { TableCell, IconButton, Link, Typography } from "@mui/material";
import Header from "../../components/headers/Header";
import SubHeader from "../../components/subHeader/SubHeader";
import InfoModal from "../../components/infoModal/InfoModal";
import ErrorModal from "../../components/error/errorModal";
import Instance from "./Instance";
import { logoutUser } from "../../features/slices/HomeSlice";
import { fetchAllProjects } from "../../features/slices/projectSlice";
import { fetchAllInstances } from "../../features/slices/allInstances";
import { fetchKba } from "../../features/slices/kbaSlice";
import { useAuthentication } from "../../constants/config/useAuthentication";
import { checkAdminUsers, logOutToken } from "../../features/slices/UserData";
import language from "../../constants/languages/en/translations.json";
import { View } from "../../constants/icons/Icons";
import "./AllProjects.scss";
import Footer from "../../constants/config/footer";
import { useMsal } from "@azure/msal-react";

const ProjectsTable = React.lazy(() => import("../../components/projectsTable/ProjectsTable"));

const signOutClickHandler = async (instance) => {
  const logoutRequest = {
    postLogoutRedirectUri: process.env.REACT_APP_BASE_URL || window.location.origin,
    navigateToLoginRequestUrl: false,
  };
  await instance.logoutRedirect(logoutRequest);
};

const useFetchData = (token, dispatch, setInstanceDataFetched) => {
  useEffect(() => {
    const fetchData = async () => {
      if (token) {
        await dispatch(fetchAllInstances({ token }));
        setInstanceDataFetched(true);
      }
    };
    fetchData();
  }, [dispatch, token, setInstanceDataFetched]);
};

const useFetchProjects = (token, dispatch, instanceDataFetched, instanceData) => {
  useEffect(() => {
    const fetchProjects = async () => {
      if (token && instanceDataFetched && instanceData?.data) {
        const instanceLinks = instanceData.data.map((instance) => instance.instanceLink);
        await dispatch(fetchAllProjects({ token, instanceLinks }));
      }
    };
    fetchProjects();
  }, [dispatch, token, instanceDataFetched, instanceData]);
};

const useCombinedData = (instanceData, allProjectsData) => {
  const [projectsByInstance, setProjectsByInstance] = useState([]);
  useEffect(() => {
    if (instanceData?.data && allProjectsData) {
      const combinedData = allProjectsData.map((project) => {
        const matchedInstance = instanceData.data.find(
          (instance) => instance.instanceLink === project.source
        );
        return {
          ...project,
          instanceName: matchedInstance?.instanceName || "Unknown Instance",
          instanceUiLink: matchedInstance?.instanceUiLink || "",
        };
      });
      setProjectsByInstance(combinedData);
    }
  }, [instanceData, allProjectsData]);
  return projectsByInstance;
};

const AllProjects = () => {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(false);
  const token = useSelector((state) => state.user.jwtToken);
  const storedLanguage = localStorage.getItem("language") ?? "EN";
  const userDetails = useAuthentication();
  const instanceData = useSelector((state) => state.allInstances.data);
  const [isEmptyModalOpen, setIsEmptyModalOpen] = useState(false);
  const isInstanceApiError = useSelector((state) => state.allInstances.isInstanceApiError);
  const isInstanceApiLoading = useSelector((state) => state.allInstances.isInstanceApiLoading);
  const instanceApiErrorMessage = useSelector(
    (state) => state.allInstances.instanceApiErrorMessage
  );
  const allProjectsData = useSelector((state) => state.allProjects?.data);
  const isLoading = useSelector((state) => state.allProjects.isLoading);
  const isError = useSelector((state) => state.isError);
  const errorMessage = useSelector((state) => state.errorMessage);
  const userState = useSelector((state) => state.user);
  const [selectedOption, setSelectedOption] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalData, setInfoModalData] = useState({
    title: "",
    description: "",
    kbaCode: "",
    languageCode: "",
  });
  const [isNavigating, setIsNavigating] = useState(false);
  const [instanceDataFetched, setInstanceDataFetched] = useState(false);
  const kbaID = "1";
  const version = 1;

  useEffect(() => {
    if (!userState.user || !token) {
      navigate("/");
      return;
    }
    if (
      instanceData?.success &&
      instanceData?.message === "NewCreatedUser" &&
      instanceData?.statusCode === 200 &&
      Array.isArray(allProjectsData) &&
      allProjectsData.length === 0
    ) {
      setUserInfo(true);
    }
    if (Array.isArray(allProjectsData) && allProjectsData.length === 0) {
      setIsEmptyModalOpen(true);
    }
  }, [userState.user, navigate, instanceData, allProjectsData, token]);

  useFetchData(token, dispatch, setInstanceDataFetched);
  useFetchProjects(token, dispatch, instanceDataFetched, instanceData);

  const projectsByInstance = useCombinedData(instanceData, allProjectsData);

  const filteredData = useMemo(() => {
    if (!searchQuery) return projectsByInstance;
    const query = searchQuery.toLowerCase();
    return projectsByInstance.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.clientName.toLowerCase().includes(query) ||
        item.instanceName.toLowerCase().includes(query)
    );
  }, [projectsByInstance, searchQuery]);

  const handleInfoClick = useCallback(
    async (language = storedLanguage) => {
      try {
        const langCode = language.toUpperCase();
        const response = await dispatch(fetchKba({ token, langCode, kbaID, version })).unwrap();
        if (response.success) {
          const result = response.data;
          setInfoModalData({
            kbaCode: `KBA #${kbaID}`,
            title: result.name,
            languageCode: result.language,
            description: result.context,
          });
          setIsInfoModalOpen(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Fetching KBA failed:", error);
        alert("Fetching KBA failed: " + (error.message || "Unknown error occurred"));
      }
    },
    [dispatch, token, storedLanguage]
  );

  const handleCloseModal = () => setIsInfoModalOpen(false);

  const handleClose = async () => {
    const controller = new AbortController();
    try {
      setIsNavigating(true);
      await new Promise((resolve) => {
        dispatch(logoutUser());
        resolve();
      });
      await new Promise((resolve, reject) => {
        dispatch(logOutToken(token))
          .then((response) => {
            if (response.error) {
              reject(response.error);
            } else {
              resolve(response);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
      localStorage.clear();
      sessionStorage.clear();
      await signOutClickHandler(instance);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error during sign-out", error);
    } finally {
      controller.abort();
      setIsNavigating(false);
    }
  };

  const handleError = () => navigate("/");

  const handleSearch = useCallback((event) => {
    setSearchQuery(event);
  }, []);

  const columnsToDisplay = useMemo(
    () => [
      { colName: "name", label: t("Project"), showSorting: true },
      { colName: "clientName", label: t("Client"), showSorting: true },
      { colName: "instanceName", label: t("Instance"), showSorting: false },
      { colName: "Action", label: t("Action"), showSorting: false },
    ],
    [t]
  );

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const toggleModal = (event) => {
    if (event.type === "click" || (event.type === "keydown" && event.key === "Enter")) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleInstanceCancel = () => setIsOpen(false);
  const handleEmptyModalClose = () => setIsEmptyModalOpen(false);

  const handleButtonClick = useCallback(
    (row) => {
      const projectId = row?.id;
      if (row.instanceUiLink && projectId && token) {
        window.location.href = `${row.instanceUiLink}/project-home/${projectId}`;
      }
    },
    [token]
  );

  const handleOnNext = useCallback(
    async (row) => {
      const { username: email } = userDetails?.userDetails || {};
      if (!email) {
        // eslint-disable-next-line no-console
        console.error("User details are not available");
        return;
      }
      try {
        setIsNavigating(true);
        const response = await dispatch(checkAdminUsers({ emailId: email, token })).unwrap();
        if (response.data === true) {
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            try {
              window.location.href = `${row.instanceUiLink}/create-new-project`;
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Error fetching AD users:", error);
            }
          }
        } else {
          alert("You are not authorized to access this page.");
          setIsNavigating(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error checking admin access:", error);
        setIsNavigating(false);
      }
    },
    [dispatch, instance, userDetails, token]
  );

  const generateRowKey = (row) => row.id;

  const getCellStyle = (column) => {
    switch (column) {
      case "Action":
        return { textAlign: "right", width: "150px", paddingRight: "7%" };
      case "instanceName":
        return { textAlign: "center", width: "215px", paddingRight: "18%" };
      default:
        return { textAlign: "left", width: "250px", paddingLeft: "2%" };
    }
  };

  const renderTableCell = (column, value, row) => {
    const cellStyle = getCellStyle(column);
    if (column === "instanceName") {
      return (
        <TableCell key={column} style={cellStyle}>
          <Link
            href={row.instanceUiLink}
            aria-label={`${row.name} project ${row.instanceName} link`}
            tabIndex={0}
          >
            {row.instanceName}
          </Link>
        </TableCell>
      );
    }
    if (column === "Action") {
      return (
        <TableCell key={column} style={cellStyle}>
          <IconButton
            onClick={() => handleButtonClick(row)}
            aria-label={`${row.name} view details button`}
          >
            <View />
          </IconButton>
        </TableCell>
      );
    }
    return (
      <TableCell key={column} style={cellStyle}>
        {value}
      </TableCell>
    );
  };

  useEffect(() => {
    setIsNavigating(false);
  }, [navigate]);

  if (isError || isInstanceApiError) {
    return (
      <ErrorModal
        setName={t("Error")}
        labelText={errorMessage || instanceApiErrorMessage}
        handleButtonClick={handleError}
        deleteButtonLabel={t("Reload")}
      />
    );
  }

  if (userInfo) {
    return (
      <ErrorModal
        setName={t("Info")}
        labelText={
          <span
            dangerouslySetInnerHTML={{
              __html:
                "Welcome! Thank you for registering. Our system is currently syncing your information, which will take approximately 30 minutes. Please check back after this time to access your account. We appreciate your patience!"
                  .split(". ")
                  .join(".<br />"),
            }}
          />
        }
        handleButtonClick={handleClose}
        deleteButtonLabel={t("Close")}
      />
    );
  }

  if (isEmptyModalOpen) {
    return (
      <ErrorModal
        setName={t("Info")}
        labelText={
          <span
            dangerouslySetInnerHTML={{
              __html:
                "You are not assigned to a project yet. In case you visit this page the first time, the directory needs ~30 minutes to synchronize your account to show your assigned projects. Please get back later to join your assigned projects."
                  .split(". ")
                  .join(".<br />"),
            }}
          />
        }
        handleButtonClick={handleEmptyModalClose}
        deleteButtonLabel={t("Close")}
      />
    );
  }

  return (
    <div className="all-projects-page">
      <Header />
      <div className="all-projects-container">
        {(isLoading || isInstanceApiLoading || isNavigating) && (
          <MotifProgressLoader
            className="loader"
            show
            variant="default"
            aria-label="Loading content"
          />
        )}
        {isOpen && (
          <Instance
            instanceHeader={t("instanceHeader")}
            instanceNext={t("instanceNext")}
            instanceModalDescription={t("instanceModalDescription")}
            selectInstance={t("selectInstance")}
            handleInstanceCancel={handleInstanceCancel}
            handleInfoClick={handleInfoClick}
            onNext={handleOnNext}
            instanceData={instanceData}
          />
        )}
        <div className="border-box">
          <SubHeader
            title={t("AllProjects")}
            selectedOption={selectedOption}
            addButtonText={language.NewProjectEN}
            onChangeSearchInput={handleSearch}
            onOptionChange={handleOptionChange}
            defaultSelection={language.DefaultEN}
            onClickNewProject={toggleModal}
            disableButton={isLoading || isInstanceApiLoading}
          />
          <Suspense fallback={<MotifProgressLoader className="loader" show variant="default" />}>
            <ProjectsTable
              columns={columnsToDisplay}
              data={filteredData}
              itemsPerPage={5}
              generateRowKey={generateRowKey}
              getCellStyle={getCellStyle}
              renderTableCell={renderTableCell}
              handleButtonClick={handleButtonClick}
            />
          </Suspense>
        </div>
      </div>
      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={handleCloseModal}
        data={infoModalData}
        handleInfoClick={handleInfoClick}
      />
      <div className="version-number-footer">
        <Typography variant="body2" align="right">
          ESG Suite 3.0
        </Typography>
        <Footer />
      </div>
    </div>
  );
};

export default AllProjects;
